/**
 * Types for AI Analysis functionality
 * טיפוסים עבור פונקציונליות ניתוח AI
 */

/**
 * AI Usage statistics
 * סטטיסטיקות שימוש ב-AI
 */
export interface AiUsage {
  callCount: number;
  monthlyLimit: number;
  periodStart: string;
  periodEnd: string;
  limitExceeded: boolean;
  remainingCalls: number;
}

/**
 * Diagnosis item from AI analysis
 * אבחנה מניתוח AI
 */
export interface AiDiagnosis {
  name: string;
  description: string;
}

/**
 * Disability section item from AI analysis
 * סעיף נכות מניתוח AI
 */
export interface AiDisabilitySection {
  section: string;
  description: string;
  percentage: number;
}

/**
 * Disability details from AI analysis
 * פרטי נכות מניתוח AI
 */
export interface AiDisabilityDetails {
  relevantSections: AiDisabilitySection[];
  calculationExplanation: string;
}

/**
 * Required document from AI analysis
 * מסמך נדרש מניתוח AI
 */
export interface AiRequiredDocument {
  name: string;
  reason: string;
}

/**
 * Key finding from AI analysis
 * ממצא עיקרי מניתוח AI
 */
export interface AiKeyFinding {
  finding: string;
  importance: 'גבוהה' | 'בינונית' | 'נמוכה' | string;
}

/**
 * Recommended action from AI analysis
 * פעולה מומלצת מניתוח AI
 */
export interface AiRecommendedAction {
  action: string;
  priority: 'דחוף' | 'רגיל' | 'עתידי' | string;
}

/**
 * AI Analysis JSON response format
 * פורמט תשובת JSON מניתוח AI
 */
export interface AiAnalysisJsonResponse {
  medicalSummary: string;
  diagnoses: AiDiagnosis[];
  estimatedDisabilityPercentage: number | null;
  disabilityDetails: AiDisabilityDetails;
  recommendedBenefitType: string;
  benefitTypeExplanation: string;
  requiredDocuments: AiRequiredDocument[];
  keyFindings: AiKeyFinding[];
  recommendedActions: AiRecommendedAction[];
  warnings: string[];
}

/**
 * Metadata sent during streaming
 * מטא-דאטה הנשלח במהלך ההזרמה
 */
export interface AiAnalysisMetadata {
  documentsAnalyzedCount: number;
  documentsAnalyzedIds: number[];
  isPartialAnalysis: boolean;
  partialAnalysisReason?: string;
}

/**
 * Request to save an analysis
 * בקשה לשמירת ניתוח
 */
export interface SaveAnalysisRequest {
  clientId: number;
  analysisContent: string;
  estimatedDisabilityPercentage?: number;
  primaryDiagnosis?: string;
  recommendedBenefitType?: string;
  requiredDocuments?: string[];
  keyFindings?: string[];
  recommendedActions?: string[];
  documentsAnalyzedCount: number;
  documentsAnalyzedIds: number[];
}

/**
 * Saved analysis from database
 * ניתוח שמור ממסד הנתונים
 */
export interface ClientAiAnalysis {
  id: number;
  clientId: number;
  analysisContent: string;
  estimatedDisabilityPercentage?: number;
  primaryDiagnosis?: string;
  recommendedBenefitType?: string;
  requiredDocuments: string[];
  keyFindings: string[];
  recommendedActions: string[];
  documentsAnalyzedCount: number;
  generatedByUserName: string;
  createdAt: string;
}

/**
 * Document preview (before analysis)
 * תצוגה מקדימה של מסמך (לפני ניתוח)
 */
export interface DocumentPreview {
  documentId: number;
  fileName: string;
  category: string;
  contentType: 'text' | 'image';
  sizeBytes: number;
  isIncludedInAnalysis: boolean;
  exclusionReason?: string;
}

/**
 * Parse JSON response and extract data for saving
 * חילוץ נתונים מתשובת JSON לשמירה
 */
export function parseAnalysisJsonForSave(json: AiAnalysisJsonResponse): {
  estimatedDisabilityPercentage?: number;
  primaryDiagnosis?: string;
  recommendedBenefitType?: string;
  requiredDocuments: string[];
  keyFindings: string[];
  recommendedActions: string[];
} {
  return {
    estimatedDisabilityPercentage: json.estimatedDisabilityPercentage ?? undefined,
    primaryDiagnosis: json.diagnoses?.[0]?.name,
    recommendedBenefitType: json.recommendedBenefitType,
    requiredDocuments: json.requiredDocuments?.map(d => d.name) ?? [],
    keyFindings: json.keyFindings?.map(f => f.finding) ?? [],
    recommendedActions: json.recommendedActions?.map(a => a.action) ?? []
  };
}
